import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Sparkles, ArrowRight, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Public, no-auth Hall of Fame — shareable link showing past monthly Spotlight winners. */
export default function HallOfFamePage() {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    document.title = "ReferPro — Hall of Fame";
    axios
      .get(`${BACKEND_URL}/api/public/hall-of-fame?limit=24`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]));
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="max-w-5xl mx-auto px-5 py-10 sm:py-16">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-brand-yellow border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal">
            <Trophy className="w-6 h-6 text-black" strokeWidth={2.6} />
          </div>
          <span className="text-xs uppercase tracking-[0.2em] font-black text-neutral-500">ReferPro Hall of Fame</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-3">
          The people who keep the lights on.
        </h1>
        <p className="text-base text-neutral-700 max-w-2xl">
          Every month we shine a light on one team-mate whose referrals make the biggest difference. Here are the legends who've earned a spot in ReferPro history.
        </p>

        {entries === null ? (
          <div className="mt-10 text-neutral-500 text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="mt-10 brutal p-8 text-center bg-white">
            <Sparkles className="w-10 h-10 mx-auto text-brand-orange" strokeWidth={2.2} />
            <p className="text-neutral-700 mt-3">No champions crowned yet — check back at the end of the month!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {entries.map((e, i) => (
              <HofCard key={`${e.month}-${i}`} entry={e} rank={i} />
            ))}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link to="/login" className="btn-brutal btn-primary" data-testid="hof-cta-login">
            Log in to ReferPro <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-neutral-500 mt-3">
            Want to be the next face here? Start referring today.
          </p>
        </div>
      </div>
    </div>
  );
}

function HofCard({ entry, rank }) {
  const gradients = [
    "linear-gradient(135deg, #FFD600 0%, #FF5C00 100%)",
    "linear-gradient(135deg, #0047FF 0%, #FF3AC5 100%)",
    "linear-gradient(135deg, #00E676 0%, #0047FF 100%)",
    "linear-gradient(135deg, #FF3AC5 0%, #FFD600 100%)",
  ];
  const bg = gradients[rank % gradients.length];
  const avatar = entry.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(entry.name)}`;

  return (
    <div className="brutal p-5 flex flex-col gap-3 relative overflow-hidden" style={{ background: bg }} data-testid={`hof-card-${entry.month}`}>
      <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-black text-black/60">
        {entry.label || entry.month}
      </div>
      <img
        src={avatar}
        alt=""
        className="w-16 h-16 rounded-2xl border-2 border-black object-cover bg-white"
      />
      <div>
        <h3 className="font-display font-black text-2xl leading-tight text-black flex items-center gap-2">
          {entry.name}
          {entry.private && <EyeOff className="w-4 h-4 text-black/60" title="Name hidden at their request" />}
        </h3>
        {entry.team && <div className="text-xs font-bold uppercase tracking-widest text-black/70 mt-0.5">{entry.team}</div>}
      </div>
      <div className="mt-auto grid grid-cols-3 gap-2">
        <StatPill label="Points" value={entry.stat_points ?? "—"} />
        <StatPill label="Referrals" value={entry.stat_referrals ?? "—"} />
        <StatPill label="Hires" value={entry.stat_hires ?? "—"} />
      </div>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="brutal bg-white/95 p-2 text-center">
      <div className="font-display font-black text-xl leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-widest font-bold text-neutral-600 mt-0.5">{label}</div>
    </div>
  );
}
